import type React from "react";
import { Navigate, Outlet, matchPath, useLocation } from "react-router-dom";

import { useSchoolyearActive } from "hooks/useSchoolyearActive";

import { GENERIC_ROUTES } from "constants/routes";
import { schoolyearAllowedRoutes } from "constants/schoolyear-constants";

/**
 *  This component contains some logic that will be applied to all application
 * routes. This will be set as the element for a parent route that must cover
 * all existing paths accross the application.
 *
 *  At the moment this simply validates whether the current path is in the
 * whitelist in case Schoolyear is active, to prevent users from accessing
 * blocked paths from that secure browser.
 *
 *  This file can be extended with any other logic that may be required on
 * all routes.
 */
const AppRouteWrapper: React.FC = () => {
  const location = useLocation();
  const { isSchoolyearActive } = useSchoolyearActive();

  // Determine whether the current location matches any of the allowed routes
  const isAllowedBySchoolyear = schoolyearAllowedRoutes.some((route) =>
    Boolean(matchPath({ path: route, end: false }, location.pathname)),
  );

  const isBlockedRoute = isSchoolyearActive && !isAllowedBySchoolyear;

  // If Schoolyear is active and route path is not whitelisted, redirect to blocked path page
  if (isBlockedRoute) {
    return <Navigate to={GENERIC_ROUTES.BLOCKED_PATH} state={{ from: location }} replace />;
  }

  // In any other case, render actual content of the page
  return <Outlet />;
};

export default AppRouteWrapper;
